/**
 * Spine configuration — the ordered navigation flow through the project.
 * Single source of truth for step labels, paths, icons, and descriptions.
 */

export interface SpineStep {
  key:
    | "summary"
    | "about-us"
    | "about-client"
    | "strategy"
    | "matrix"
    | "drafts"
    | "attachments"
    | "qa";
  label: string;
  path: string; // relative to /projects/:id, starts with /
  icon: string; // name of a lucide-react icon (resolved in consumer components)
  shortDescription: string; // for tooltips
}

export const SPINE: readonly SpineStep[] = [
  {
    key: "summary",
    label: "Summary",
    path: "/summary",
    icon: "FileText",
    shortDescription: "Key details pulled from the RFP",
  },
  {
    key: "about-us",
    label: "About Us",
    path: "/about-us",
    icon: "Building2",
    shortDescription: "Your agency's positioning, voice, and past work",
  },
  {
    key: "about-client",
    label: "About Client",
    path: "/about-client",
    icon: "Users",
    shortDescription: "What you know about the prospect",
  },
  {
    key: "strategy",
    label: "Strategy",
    path: "/strategy",
    icon: "Lightbulb",
    shortDescription: "The angle and themes for this proposal",
  },
  {
    key: "matrix",
    label: "Requirements Tracker",
    path: "/matrix",
    icon: "Table2",
    shortDescription: "Everything the RFP asks you to answer",
  },
  {
    key: "drafts",
    label: "RFP Response",
    path: "/drafts",
    icon: "PenTool",
    shortDescription: "Compose the response — sections, drafts, assembly",
  },
  {
    key: "attachments",
    label: "Attachments",
    path: "/attachments",
    icon: "FolderCheck",
    shortDescription: "Required supporting documents",
  },
  {
    key: "qa",
    label: "Final Check",
    path: "/qa",
    icon: "ShieldCheck",
    shortDescription: "AI review before you submit",
  },
] as const;

/**
 * Get the next step in the spine, or null if already at the end.
 */
export function nextStep(currentKey: SpineStep["key"]): SpineStep | null {
  const i = SPINE.findIndex((s) => s.key === currentKey);
  return i >= 0 && i < SPINE.length - 1 ? SPINE[i + 1] : null;
}

/**
 * Get the previous step in the spine, or null if already at the start.
 */
export function prevStep(currentKey: SpineStep["key"]): SpineStep | null {
  const i = SPINE.findIndex((s) => s.key === currentKey);
  return i > 0 ? SPINE[i - 1] : null;
}
